import { axios, RawAxiosHeaders } from '../interceptors';
import {
  CustomerDetails,
  DeleteSwitchRequest,
  SearchByAccountNo,
  Switch,
  TestingDashboardLoginResponse,
  VerifySwitch,
  TestingDashboardBillEstimationResponse,
  BillLineItem,
  ClearRedisCache,
} from '../models';
import { UtilService } from './util-service';

class TestingDashboardService {
  private static baseUrl = '/testing-dashboard';

  public static deleteSwitchRequest = async (servicePointId: string) => {
    const response = await axios.delete<DeleteSwitchRequest>(`${this.baseUrl}/switch-request`, {
      params: {
        spId: servicePointId,
      },
    });
    return response.data;
  };

  public static searchByAccountNo = async (accountNumber: string) => {
    const response = await axios.get<SearchByAccountNo>(`${this.baseUrl}/account`, {
      params: {
        accountNumber,
      },
    });
    return response.data;
  };

  public static getBillEstimate = async (
    servicePointId: string,
    accountNumber: string,
  ) => {
    const response = await axios.get<TestingDashboardBillEstimationResponse[]>(`${this.baseUrl}/account/estimate`, {
      params: {
        sp: servicePointId,
        accountNumber,
      },
    });
    return response.data;
  };

  public static getBillLineItems = async (servicePointId: string) => {
    const response = await axios.get<[string, BillLineItem[]][]>(`${this.baseUrl}/bill/line-items`, {
      params: {
        servicePointId,
      },
    });
    return response.data;
  };

  public static getAllSwitchRequests = async () => {
    const response = await axios.get<Switch[]>(`${this.baseUrl}/switch-requests`);
    return response.data;
  };

  public static clearRedisCache = async () => {
    const response = await axios.get<ClearRedisCache>(`${this.baseUrl}/clear-redis-cache`);
    return response.data;
  };

  public static searchByPlan = async (ratePlan: string, onlyValid: boolean) => {
    const response = await axios.get<CustomerDetails>(`${this.baseUrl}/plan`, {
      params: {
        ratePlan,
        onlyValid,
      },
    });
    return response.data;
  };

  public static async authorize(userId: string, password: string, featureEnabled: boolean | undefined) {
    let headers = undefined as RawAxiosHeaders | undefined;
    if (featureEnabled) {
      headers = UtilService.getEnableFeatureHeader('TESTING_DASHBOARD');
    }

    const res = await axios.post<TestingDashboardLoginResponse>(`${this.baseUrl}/login`, { userId, password }, headers);
    return res.data;
  }

  public static verifySwitch = async (switchDate: Date, servicePointId: string) => {
    const response = await axios.get<VerifySwitch>(`${this.baseUrl}/verify-switch`, {
      params: {
        spId: servicePointId,
      },
    });
    return response.data.EFFDT > switchDate;
  };

  public static searchBySpId = async (spID: string) => {
    const response = await axios.get<CustomerDetails>(`${this.baseUrl}/service-point`, {
      params: {
        spID,
      },
    });
    return response.data;
  };

  public static searchByMeter = async (meterId: string) => {
    const response = await axios.get<CustomerDetails>(`${this.baseUrl}/meter`, {
      params: {
        meterId,
      },
    });
    return response.data;
  };

  public static searchByServiceLength = async (stateCode: string) => {
    const response = await axios.get<CustomerDetails>(`${this.baseUrl}/service-length`, {
      params: {
        stateCode,
      },
    });
    return response.data;
  };

  public static getAccountWithPaymentArrangement = async (onlyValid: boolean, stateCode: string) => {
    const response = await axios.get<CustomerDetails>(`${this.baseUrl}/payment-arrangement`, {
      params: {
        onlyValid,
        stateCode,
      },
    });
    return response.data;
  };

  public static getCustomerWithFixedPlan = async (stateCode: string) => {
    const response = await axios.get<CustomerDetails>(`${this.baseUrl}/customer/fixed-plan`, {
      params: {
        stateCode,
      },
    });
    return response.data;
  };

  public static getAccountWithBudgetPlan = async (onlyValid: boolean, stateCode: string) => {
    const response = await axios.get<CustomerDetails>(`${this.baseUrl}/budget-plan`, {
      params: {
        onlyValid,
        stateCode,
      },
    });
    return response.data;
  };

  public static getNoActiveMeterAccount = async (stateCode: string) => {
    const response = await axios.get<CustomerDetails>(`${this.baseUrl}/customer/no-active-meter`, {
      params: {
        stateCode,
      },
    });
    return response.data;
  };

  public static getAccountWithLessAnnualUsage = async (stateCode: string) => {
    const response = await axios.get<CustomerDetails>(`${this.baseUrl}/customer/less-annual-usage`, {
      params: {
        stateCode,
      },
    });
    return response.data;
  };

  public static getAccountThatIneligibleToSwitch = async (stateCode: string) => {
    const response = await axios.get<CustomerDetails>(`${this.baseUrl}/customer/ineligible-to-switch`, {
      params: {
        stateCode,
      },
    });
    return response.data;
  };

  public static getAccountWithSeverance = async (onlyValid: boolean, stateCode: string) => {
    const response = await axios.get<CustomerDetails>(`${this.baseUrl}/customer/severance`, {
      params: {
        onlyValid,
        stateCode,
      },
    });
    return response.data;
  };

  public static getAccountHavingHighAnnualUsage = async (onlyValid: boolean, stateCode: string) => {
    const response = await axios.get<CustomerDetails>(`${this.baseUrl}/customer/high-annual-usage`, {
      params: {
        onlyValid,
        stateCode,
      },
    });
    return response.data;
  };

  public static getAccountWithRecentlySwitched = async (stateCode: string) => {
    const response = await axios.get<CustomerDetails>(`${this.baseUrl}/customer/recently-switched`, {
      params: {
        stateCode,
      },
    });
    return response.data;
  };

  public static getMoreThanTenPercentageDifference = async (stateCode: string) => {
    const response = await axios.get<CustomerDetails>(`${this.baseUrl}/customer/billed-calculated-uasge-difference`, {
      params: {
        stateCode,
      },
    });
    return response.data;
  };
}

export { TestingDashboardService };
